/**
 * The panel's single poll — §14.2.1 / §14.7.
 *
 * `GET /admin/summary` is deliberately five things at once: the dashboard's
 * numbers, the bell's notification source, the top bar's clock, the online
 * fleet widget and the attention centre. It is polled HERE, once, in a provider
 * mounted by the shell, and every consumer reads the same response — because
 * the alternative is four components each opening their own 60-second interval
 * against the same endpoint.
 *
 * Nothing is polled for a session without `analytics.read`: a `content_editor`
 * signs in to edit the directory and must not generate a request per minute
 * against an endpoint it is not permitted to read.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { components } from '@navoiy/api-types';
import { ApiError, api, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useI18n } from '../i18n/LocaleContext.jsx';

export type AdminSummary = components['schemas']['AdminSummary'];

/** §14.7 — the panel polls at 60 s, which is also the server-side memo's TTL. */
const POLL_MS = 60_000;

interface SummaryContextValue {
  summary: AdminSummary | null;
  loading: boolean;
  error: boolean;
  /**
   * The server's clock, advanced locally between polls.
   *
   * §14.7 is explicit that this is **not** `new Date()`: a government panel's
   * wall clock is the server's clock. A staff machine with a wrong RTC would
   * otherwise render a deadline as overdue that the server considers open, and
   * the panel would disagree with its own arithmetic.
   */
  serverNow: Date | null;
  reload: () => void;
}

const SummaryContext = createContext<SummaryContextValue | null>(null);

export function SummaryProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { allows, expire } = useSession();
  const { locale } = useI18n();
  const enabled = allows('analytics.read');

  const [summary, setSummary] = useState<AdminSummary | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(false);
  /** The instant the current `server_time` was received, by the local clock. */
  const [anchor, setAnchor] = useState<{ server: number; local: number } | null>(null);
  /**
   * The local clock, sampled once a second. Only ever used as an OFFSET from
   * `anchor.local`, never as a time in its own right — so a machine whose clock
   * is wrong by an hour still renders the server's time correctly, and only its
   * between-poll drift (a second at most) comes from the local oscillator.
   */
  const [localNow, setLocalNow] = useState(() => Date.now());
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const data = unwrap(await api().GET('/admin/summary', { params: { query: { locale } } }));
        if (cancelled) return;
        setSummary(data);
        setAnchor({ server: new Date(data.server_time).getTime(), local: Date.now() });
        setError(false);
      } catch (caught) {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 401) {
          expire();
          return;
        }
        // A failed poll leaves the LAST GOOD numbers on screen and raises the
        // flag; blanking the dashboard because one minute's request failed
        // would be a worse lie than showing a figure that is a minute old.
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [enabled, locale, expire, nonce]);

  useEffect(() => {
    if (!anchor) return;
    const timer = window.setInterval(() => setLocalNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [anchor]);

  const value = useMemo<SummaryContextValue>(
    () => ({
      summary,
      loading,
      error,
      serverNow: anchor ? new Date(anchor.server + (localNow - anchor.local)) : null,
      reload,
    }),
    [summary, loading, error, anchor, localNow, reload],
  );

  return <SummaryContext.Provider value={value}>{children}</SummaryContext.Provider>;
}

export function useSummary(): SummaryContextValue {
  const context = useContext(SummaryContext);
  if (!context) throw new Error('useSummary used outside SummaryProvider');
  return context;
}
